import {all, delay, put, select, takeEvery, takeLatest, takeLeading} from 'redux-saga/effects';
import * as Sentry from '@sentry/browser';

import {
    selectCurrentUser,
    selectEnqueueSnackbar,
    selectIsConnected,
    selectWsInit,
} from '../selectors/app.selectors';
import {
    APP_LOGIN_ADMIN,
    APP_LOGOUT,
    APP_MOUNTED,
    CHECK_JWT,
    CONNECTION_CHANGED,
    ENQUEUE_SNACKBAR,
    FORCE_RELOAD,
    GET_DATA,
    KEEP_ALIVE,
    LANGUAGE_CHANGED,
    NEW_ADMIN_TOKEN,
    NEW_TOKEN,
    RECONNECT_WEBSOCKET, REFRESH_SSO_TOKEN,
    RETRY_GET_DATA, SET_IS_ONLINE,
    USER_CHANGED,
} from '../constants/app.constants';
import {MyWebSocket} from '../../utils/MyWebSocket';
import BaseAPI from '../../utils/API/BaseAPI';
import {deviceSagas} from './device.saga';
import {alarmSagas} from './alarm.saga';
import {dashboardSagas} from './dashboard.saga';
import {teamSagas} from './team.saga';
import {scenarioSagas} from './scenario.saga';
import {accountSagas} from './account.saga';
import {geofenceSagas} from './geofence.saga';
import {beaconSagas} from './beacon.saga';
import {siteSagas} from './site.saga';
import {eventSagas} from './event.saga';
import {assistanceRequestSagas} from './assistanceRequest.saga';
import User from '../../utils/models/User';
import {AdminAPI} from '../../utils/API/AdminAPI';
import {
    logout,
    newTokenAction,
    retryGetData as retryGetDataAction, setAppEnabled, toggleDisconnectedSound,
    userChanged,
} from '../actions/app.actions';
import i18next from 'i18next';
import {UserAPI} from '../../utils/API/UserAPI';
import dayjs from 'dayjs';
import {
    WS_DATA_ALARM,
    WS_DATA_ALARMS, WS_DATA_ASS_REQUEST,
    WS_DATA_ASS_REQUESTS,
    WS_FORCE_RELOAD,
    WS_REQUEST_RELOAD_DATA
} from '../constants/alarm.constants';
import {showAlert} from '../../containers/AlertDialog/actions';
import {getI18n} from 'react-i18next';
import {getActivePage, getLangFromLocale} from '../../utils/functions';
import jwtDecode from 'jwt-decode';
import {getStore} from '../../configureStore';
import {getWhitelabel, isLive} from '../../env';
import {AUTH_SUCCESS} from '../../containers/LoginPage/constants';
import {getNavigator} from '../../Navigator';
import {selectOpenAlarms} from "../selectors/alarm.selector";
import {selectOpenAssistanceRequests} from "../selectors/assistanceRequest.selector";
import {selectOptions} from "../../containers/PresentationPage/selectors";
import {userSagas} from "./user.saga";
import {supportSagas} from "./support.saga";
import {activitySagas} from "./activity.saga";
import {attendanceMinimumsSagas} from "./attendanceMinimums.saga";
import {authSuccess} from "../../containers/LoginPage/actions";
import {subscribeToWsTopics} from "../actions/dashboard.actions";
import {consoleError, consoleWarn} from "../../utils/log";
import {getFreshIdpAccessToken, logoutSSO} from "../../msal";
import {InteractionRequiredAuthError} from "@azure/msal-browser";

global.toPage = page => {
    if (isLive()) return;
    getNavigator()(page);
};
global.myDispatch = action => {
    if (isLive()) return;
    getStore().dispatch(action);
};

const api = new BaseAPI();
const adminApi = new AdminAPI();
const userApi = new UserAPI();

function* saveUserSaga({store}) {
    const user = yield select(selectCurrentUser);
    if (store !== false && !BaseAPI.inMemoryToken) {
        localStorage.setItem('user', JSON.stringify(user.toJS()));
    } else {
        BaseAPI.inMemoryToken = user.token;
    }
}

function* loadWebSocketSaga(props) {
    const force = !!props?.force;
    const delayMs = props?.delayMs || 0;
    const websocket = MyWebSocket.inst();
    const isOpen = yield websocket?.isOpen();
    if (isOpen && !force) return;
    yield delay(delayMs || 0);
    const user = yield select(selectCurrentUser);
    if (user) yield loginWebSocketSaga();
}

function* loginWebSocketSaga() {
    yield MyWebSocket.inst().restart();
    const user = yield select(selectCurrentUser);
    MyWebSocket.inst().subscribe('requestReloadData');
    const page = yield select(selectOptions);
    if(user.isPresentation) {
        yield put(subscribeToWsTopics());
       return; 
    }
    if(!page) {
        MyWebSocket.inst().subscribe('*');
    }
}

let dataRetryTimeout = null;

function* connectionChangedSaga({isConnected}) {
    if (dataRetryTimeout) clearTimeout(dataRetryTimeout);
    const user: User = yield select(selectCurrentUser);
    if (isConnected && user) {
        yield delay(300);
        yield api.requestData();
        dataRetryTimeout = setTimeout(() => {
            getStore().dispatch(retryGetDataAction());
        }, 3000);
    }
}

function* retryGetDataSaga() {
    const websocketInitialized = yield select(selectWsInit);
    if (websocketInitialized) return;
    yield getDataSaga();
}

function* getDataSaga() {
    const user: User = yield select(selectCurrentUser);
    if (user.isUser || user.isPresentation) {
        yield api.requestData();
    }
}

function* redirectUserSaga() {
    const user: User = yield select(selectCurrentUser);
    if (user && !user.lastLogin && !user.isPresentation && !user.entraTenantId) {
        getNavigator()('/newPassword');
        return;
    }
    const page = getActivePage();
    if (page.indexOf('admin') >= 0 && !user.isSuperAdmin) {
        return getNavigator()('/');
    }
}

function* logoutSaga({isExpired, opts}) {
    yield Promise.resolve();
    if (MyWebSocket.inst()) {
        MyWebSocket.inst().close();
    }
    const wasAdmin = !!localStorage.getItem('adminUser');
    localStorage.removeItem('user');
    localStorage.removeItem('adminUser');
    localStorage.removeItem('agreedToTermsOfUse');
    localStorage.removeItem('rt');
    Sentry.configureScope(scope => scope.setUser(null));
    yield logoutSSO();
    if(opts?.redirect) {
        window.location.href = opts.redirect;
        return;
    }
    if (isExpired) {
        const user = yield select(selectCurrentUser);
        const wasPresentation = user.isPresentation;
        //because of potential javascript errors, reload the page
        if (wasAdmin) {
            window.location.href = '/?msg=adminSessionExpiredMessage&type=warning';
        } else if (wasPresentation) {
            window.location.reload();
        } else {
            window.location.href = '/?msg=sessionExpiredMessage&type=warning';
        }
    } else {
        window.location.href = '/';
    }
}

function* loginAdminSaga() {
    const user = yield select(selectCurrentUser);
    localStorage.removeItem('agreedToTermsOfUse');
    yield adminApi.loginAdmin();
    if(user?.isUser) {
        window.location.href = `/admin/devices/${user.accountId}`;
    } else {
        window.location.href = `/`;
    }
}
function* setLanguageSaga() {
    const user = yield select(selectCurrentUser);
    const newLang = user.get('language');
    if (i18next.language !== newLang) {
        yield i18next.changeLanguage(getLangFromLocale(newLang));
    }
}

function* saveLanguageSaga({language, callback}) {
    const lang = getLangFromLocale(language);
    dayjs.locale(lang);
    const user = yield select(selectCurrentUser);
    if (user && !user.viaAdmin) {
        yield userApi.saveLanguage(language);
    }
    if(callback) callback();
    if (i18next.language !== language) {
        yield i18next.changeLanguage(lang);
    }
}

function* requestForceReloadSaga({doLogout}) {
    yield adminApi.forceReload(doLogout);
}

function* forceReloadSaga({data}) {
    const i18n = getI18n();
    let text = i18n.t('forceReloadWarning.1');
    if (+data === 1) {
        text += `\n${i18n.t('forceReloadWarning.2')}`;
    }
    yield put(showAlert(
        text,
        i18n.t('reload'), {
            disableBackdropClick: true,
            confirmText: i18n.t('reload'),
            onConfirm: () => {
                if (+data === 1) {
                    put(logout());
                }
                window.location.reload();
            },
        },
    ));
}

function* doEnqueueSnackbarSaga({input, attrs}) {
    const enqueueSnackbar = yield select(selectEnqueueSnackbar);
    enqueueSnackbar(input, attrs);
}

function* checkForAlertSaga() {
    const urlParams = new URLSearchParams(window.location.search);
    const alert = urlParams.get('alert');
    if (alert) {
        getNavigator()(window.location.pathname);
        const i18n = getI18n();
        yield put(showAlert(i18n.t(alert), i18n.t('alert')));
    }
}

function* checkJWTSaga() {
    const user = yield select(selectCurrentUser);
    if (!user) return;
    const token = BaseAPI.token;
    try {
        let {exp} = jwtDecode(token);
        if (exp < dayjs().unix()) {
            if (localStorage.getItem('rememberMe') === 'yes') {
                yield put(setAppEnabled(false));
                yield api.refreshJWT();
            } else {
                yield put(logout(true));
                return;
            }
        }
    } catch (_) {
    } finally {
        yield put(setAppEnabled(true));
    }
    const isConnected = yield select(selectIsConnected);
    if (!isConnected) {
        yield loginWebSocketSaga();
    }
}

function* keepAliveSaga() {
    const user = yield select(selectCurrentUser);
    if (!user) return;
    try {
        const {token, adminToken} = yield api.keepAlive();
        if (!token) return;
        yield put(newTokenAction(token));
        if (adminToken) {
            yield saveNewAdminTokenSaga({token: adminToken});
        }
    } catch (_) {
        // token could not be restored; the user simply has to log in again
    }
}

function* saveNewTokenSaga({token}) {
    const decoded = jwtDecode(token);
    decoded.user.token = token;
    const user = new User(decoded.user);
    localStorage.setItem('user', JSON.stringify(user.toJS()));
    yield put(userChanged(user));
    MyWebSocket.inst().token = token;
}

function* refreshSSOTokenSaga() {
    const user = yield select(selectCurrentUser);
    if(!user.entraTenantId) return;
    if(user.isShadow) return;
    try {
        const idpAccessToken = yield getFreshIdpAccessToken({forceRefresh: true});
        const newToken = yield userApi.updateIdpAccessToken(idpAccessToken);
        if(newToken) yield saveNewTokenSaga({token: newToken});
    } catch (e) {
        if(e.status === 401 || e instanceof InteractionRequiredAuthError) {
            yield put(logout(false, {
                redirect: '/?msg=sso.error.noRoles&type=warning'
            }));
        }
        consoleError(e);
    }
}

function* saveNewAdminTokenSaga({token}) {
    yield Promise.resolve();
    const decoded = jwtDecode(token);
    decoded.user.token = token;
    const user = new User(decoded.user);
    localStorage.setItem('adminUser', JSON.stringify(user.toJS()));
}

function* reconnectWsAfterMountSaga() {
    yield delay(1000);
    yield loadWebSocketSaga();
}

function *identifySentrySaga() {
    try {
        const user = yield select(selectCurrentUser);
        if (!user) return;
        const shadowUser = localStorage.getItem('adminUser');
        // Allowlist only: no e-mail, name or account name may reach Sentry.
        const identity = {
            id: String(user.id),
            accountId: user.accountId,
            impersonated: !!shadowUser,
        };
        if (shadowUser) {
            const adminUserObj = JSON.parse(shadowUser);
            identity.impersonatorId = adminUserObj.id;
        }
        Sentry.setUser(identity);
    } catch (e) {
        consoleWarn('could not login sentry')
        consoleWarn(e);
    }
}

function *refreshDocumentTitleSaga() {
    const user = yield select(selectCurrentUser);
    if(!user) return;
    const i18n = getI18n();
    const isConnected = yield select(selectIsConnected);
    if(!isConnected || !navigator.onLine) {
        return document.title = `!! ${i18n.t('connectionLost')} !!`;
    }
    if(!user.isPresentation) {
        const openAlarms = yield select(selectOpenAlarms);
        if (!!openAlarms.size) {
            return document.title = `!! ${i18n.t('openAlarms')} !!`;
        }
        const openAssistanceRequests = yield select(selectOpenAssistanceRequests);
        if (!!openAssistanceRequests.size) {
            return document.title = `!! ${i18n.t('pendingAssistanceRequests')} !!`;
        }
    }
    const whitelabel = getWhitelabel();
    document.title = whitelabel.platformName || 'Web app';
}

function *setSoundSettingsSaga() {
    const user = yield select(selectCurrentUser);
    if(user?.isARC !== true) return;
    yield put(toggleDisconnectedSound(true));
}

function *downloadFileSaga() {
    const hash = window.location.hash;
    if(hash?.indexOf('#dwnld=') !== 0) return;
    window.location.hash = '#';
    const url = hash.replace('#dwnld=', '');
    window.open(url);
    const t = getI18n().t;
    const body = t('yourFileIsDownloading');
    yield put(showAlert(body));
}

function* tryLoginPublicViaTokenSaga() {
    const currentUser = yield select(selectCurrentUser);
    if(currentUser) return;

    if(!window.location.pathname.match(/\/live-location\/.*/)) return;
    const token = window.location.pathname.split('/')[2];
    const user: User = yield api.loginPublicLiveLocationWithToken(token);
    yield put(authSuccess(user, false));
}

function* appSaga() {
    yield takeEvery(GET_DATA, getDataSaga);
    yield takeEvery(WS_REQUEST_RELOAD_DATA, getDataSaga);
    yield takeEvery(RETRY_GET_DATA, retryGetDataSaga);
    yield takeEvery(WS_FORCE_RELOAD, forceReloadSaga);
    yield takeEvery(FORCE_RELOAD, requestForceReloadSaga);
    yield takeEvery(USER_CHANGED, saveUserSaga);
    yield takeEvery(AUTH_SUCCESS, saveUserSaga);
    yield takeEvery(AUTH_SUCCESS, redirectUserSaga);
    yield takeEvery(AUTH_SUCCESS, loginWebSocketSaga);
    yield takeEvery(AUTH_SUCCESS, setLanguageSaga);
    yield takeEvery(AUTH_SUCCESS, setSoundSettingsSaga);
    yield takeEvery(APP_MOUNTED, setSoundSettingsSaga);
    yield takeLatest(LANGUAGE_CHANGED, saveLanguageSaga);
    yield takeEvery(APP_MOUNTED, redirectUserSaga);
    yield takeEvery(APP_MOUNTED, loadWebSocketSaga);
    yield takeEvery(APP_MOUNTED, reconnectWsAfterMountSaga);
    yield takeEvery(APP_MOUNTED, checkForAlertSaga);
    yield takeEvery(NEW_TOKEN, saveNewTokenSaga);
    yield takeEvery(NEW_ADMIN_TOKEN, saveNewAdminTokenSaga);
    yield takeEvery(CHECK_JWT, checkJWTSaga);
    yield takeEvery(APP_LOGOUT, logoutSaga);
    yield takeLatest(APP_LOGIN_ADMIN, loginAdminSaga);
    yield takeEvery(CONNECTION_CHANGED, connectionChangedSaga);
    yield takeEvery(ENQUEUE_SNACKBAR, doEnqueueSnackbarSaga);
    yield takeEvery(KEEP_ALIVE, keepAliveSaga);
    yield takeLeading(RECONNECT_WEBSOCKET, loadWebSocketSaga);
    yield takeEvery(APP_MOUNTED, identifySentrySaga);
    yield takeEvery(AUTH_SUCCESS, identifySentrySaga);
    
    yield takeEvery(WS_DATA_ALARMS, refreshDocumentTitleSaga);
    yield takeEvery(WS_DATA_ALARM, refreshDocumentTitleSaga);
    yield takeEvery(WS_DATA_ASS_REQUESTS, refreshDocumentTitleSaga);
    yield takeEvery(WS_DATA_ASS_REQUEST, refreshDocumentTitleSaga);
    yield takeEvery(CONNECTION_CHANGED, refreshDocumentTitleSaga);
    yield takeEvery(SET_IS_ONLINE, refreshDocumentTitleSaga);
    yield takeEvery(APP_MOUNTED, downloadFileSaga);
    yield takeLatest(APP_MOUNTED, tryLoginPublicViaTokenSaga);
    yield takeLatest(REFRESH_SSO_TOKEN, refreshSSOTokenSaga)
    
    yield all(accountSagas);
    yield all(deviceSagas);
    yield all(alarmSagas);
    yield all(dashboardSagas);
    yield all(teamSagas);
    yield all(scenarioSagas);
    yield all(geofenceSagas);
    yield all(beaconSagas);
    yield all(siteSagas);
    yield all(eventSagas);
    yield all(assistanceRequestSagas);
    yield all(userSagas);
    yield all(supportSagas);
    yield all(activitySagas);
    yield all(attendanceMinimumsSagas);
}

export default appSaga;